import React, { useState, useEffect } from "react";
import {
  XMarkIcon,
  CommandLineIcon,
  PlayIcon,
  DocumentTextIcon,
  EnvelopeIcon,
  CogIcon,
  BellIcon,
} from "@heroicons/react/24/outline";
import LoadingSpinner from "../ui/LoadingSpinner";

export default function AutomationForm({
  isOpen,
  onClose,
  onSave,
  automation = null,
  templates = [],
  loading = false,
}) {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    type: "email",
    status: "draft",
    triggers: [],
    config: {},
    templateId: "",
  });

  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [errors, setErrors] = useState({});

  // Reset form when modal opens/closes or automation changes
  useEffect(() => {
    if (isOpen) {
      if (automation) {
        // Editing existing automation
        setFormData({
          name: automation.name || "",
          description: automation.description || "",
          type: automation.type || "email",
          status: automation.status || "draft",
          triggers: automation.triggers || [],
          config: automation.config || {},
          templateId: automation.templateId || "",
        });
      } else {
        // Creating new automation
        setFormData({
          name: "",
          description: "",
          type: "email",
          status: "draft",
          triggers: [],
          config: {},
          templateId: "",
        });
      }
      setErrors({});
      setSelectedTemplate(null);
    }
  }, [isOpen, automation]);

  // Update form when template is selected
  useEffect(() => {
    if (selectedTemplate) {
      setFormData((prev) => ({
        ...prev,
        name: selectedTemplate.name.replace(" Template", ""),
        description: selectedTemplate.description,
        type: selectedTemplate.type,
        triggers: selectedTemplate.supportedTriggers || [],
        templateId: selectedTemplate.id,
      }));
    }
  }, [selectedTemplate]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when field is updated
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: null,
      }));
    }
  };

  const handleTriggersChange = (trigger, checked) => {
    setFormData((prev) => ({
      ...prev,
      triggers: checked
        ? [...prev.triggers, trigger]
        : prev.triggers.filter((t) => t !== trigger),
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Automation name is required";
    }

    if (!formData.description.trim()) {
      newErrors.description = "Description is required";
    }

    if (formData.triggers.length === 0) {
      newErrors.triggers = "At least one trigger must be selected";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const automationData = {
      ...formData,
      id: automation?.id || `auto-${Date.now()}`,
      createdAt: automation?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      totalRuns: automation?.totalRuns || 0,
      inProgress: automation?.inProgress || 0,
      successfulRuns: automation?.successfulRuns || 0,
      failedRuns: automation?.failedRuns || 0,
    };

    onSave(automationData);
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case "email":
        return <EnvelopeIcon className="h-5 w-5" />;
      case "document":
        return <DocumentTextIcon className="h-5 w-5" />;
      case "data_processing":
        return <CogIcon className="h-5 w-5" />;
      case "notification":
        return <BellIcon className="h-5 w-5" />;
      default:
        return <CommandLineIcon className="h-5 w-5" />;
    }
  };

  const availableTriggers = [
    {
      id: "valuation_completed",
      name: "Valuation Completed",
      description: "When a property valuation is marked as completed",
    },
    {
      id: "indicative_offer_accepted",
      name: "Indicative Offer Accepted",
      description: "When an indicative offer receives acceptance",
    },
    {
      id: "deal_status_changed",
      name: "Deal Status Changed",
      description: "When a deal's status is updated",
    },
    {
      id: "deal_milestone_reached",
      name: "Deal Milestone Reached",
      description: "When a deal reaches a specific milestone",
    },
    {
      id: "property_added",
      name: "Property Added",
      description: "When a new property is added to the system",
    },
    {
      id: "scheduled_daily",
      name: "Daily Schedule",
      description: "Triggered daily at specified time",
    },
    {
      id: "manual_trigger",
      name: "Manual Trigger",
      description: "Manually triggered by user action",
    },
  ];

  const automationTypes = [
    {
      value: "email",
      label: "Email Automation",
      description: "Send automated emails",
    },
    {
      value: "document",
      label: "Document Generation",
      description: "Generate and send documents",
    },
    {
      value: "data_processing",
      label: "Data Processing",
      description: "Process and enrich data",
    },
    {
      value: "notification",
      label: "Notifications",
      description: "Send system notifications",
    },
  ];

  const statusOptions = [
    { value: "draft", label: "Draft", description: "Not yet active" },
    {
      value: "active",
      label: "Active",
      description: "Running and available for triggers",
    },
    { value: "paused", label: "Paused", description: "Temporarily disabled" },
    { value: "archived", label: "Archived", description: "No longer in use" },
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <CommandLineIcon className="h-6 w-6 text-indigo-600" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {automation ? "Edit Automation" : "Create New Automation"}
              </h2>
              <p className="text-sm text-gray-600">
                {automation
                  ? "Update automation settings"
                  : "Set up a new automated workflow"}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="p-6 overflow-y-auto max-h-[calc(90vh-12rem)]"
        >
          {/* Template Selection (for new automations) */}
          {!automation && templates.length > 0 && (
            <div className="mb-6">
              <h3 className="text-md font-medium text-gray-900 mb-3">
                Start from Template (Optional)
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {templates.map((template) => (
                  <div
                    key={template.id}
                    onClick={() => setSelectedTemplate(template)}
                    className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                      selectedTemplate?.id === template.id
                        ? "border-indigo-500 bg-indigo-50"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      {getTypeIcon(template.type)}
                      <div>
                        <p className="font-medium text-gray-900">
                          {template.name}
                        </p>
                        <p className="text-sm text-gray-600">
                          {template.description}
                        </p>
                        <p className="text-xs text-gray-500">
                          Type: {template.type} • Complexity:{" "}
                          {template.complexity}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="text-md font-medium text-gray-900">
                Basic Information
              </h3>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Automation Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                    errors.name ? "border-red-500" : "border-gray-300"
                  }`}
                  placeholder="e.g., Send Welcome Email"
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description *
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    handleInputChange("description", e.target.value)
                  }
                  rows={3}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                    errors.description ? "border-red-500" : "border-gray-300"
                  }`}
                  placeholder="Describe what this automation does..."
                />
                {errors.description && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.description}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Automation Type
                </label>
                <select
                  value={formData.type}
                  onChange={(e) => handleInputChange("type", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  {automationTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => handleInputChange("status", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  {statusOptions.map((status) => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Triggers */}
            <div className="space-y-4">
              <h3 className="text-md font-medium text-gray-900">Triggers *</h3>
              <p className="text-sm text-gray-600">
                Select when this automation should run
              </p>

              <div className="space-y-3 max-h-64 overflow-y-auto">
                {availableTriggers.map((trigger) => (
                  <div key={trigger.id} className="flex items-start space-x-3">
                    <input
                      type="checkbox"
                      id={trigger.id}
                      checked={formData.triggers.includes(trigger.id)}
                      onChange={(e) =>
                        handleTriggersChange(trigger.id, e.target.checked)
                      }
                      className="mt-1 h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                    />
                    <div className="flex-1">
                      <label
                        htmlFor={trigger.id}
                        className="text-sm font-medium text-gray-900 cursor-pointer"
                      >
                        {trigger.name}
                      </label>
                      <p className="text-xs text-gray-600">
                        {trigger.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {errors.triggers && (
                <p className="text-sm text-red-600">{errors.triggers}</p>
              )}
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex items-center justify-end space-x-3 mt-8 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex items-center space-x-2 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
              disabled={loading}
            >
              {loading && <LoadingSpinner size="small" color="white" />}
              <span>
                {automation ? "Update Automation" : "Create Automation"}
              </span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
